import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import {
  DataTableColumnHeader,
  ImageCell,
  TextCell,
  ExpandCell,
  ActionsCell,
  makeSelectColumn,
} from "@/components/data-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import type { PrepRecipeRow } from "./types";

export interface PrepRecipeRowCallbacks {
  onEdit: (row: PrepRecipeRow) => void;
  onDelete: (row: PrepRecipeRow) => void;
}

interface PrepRecipeColumnOptions {
  /** When true, adds expander + ID column for panel view */
  expandable?: boolean;
  /** Show organization column in list/table views. */
  showOrganization?: boolean;
  /** Show company column in list/table views. */
  showCompany?: boolean;
  callbacks?: PrepRecipeRowCallbacks;
  /** When true, leaves out the row actions column (Edit / Delete): the list is view-only. */
  readOnly?: boolean;
  /** When true, prepends a checkbox column for multi-row selection */
  selectable?: boolean;
}

export function getPrepRecipeColumns(
  i18n: I18n,
  options: PrepRecipeColumnOptions = {},
): ColumnDef<PrepRecipeRow, unknown>[] {
  const cols: ColumnDef<PrepRecipeRow, unknown>[] = [];

  if (options.selectable) {
    cols.push(makeSelectColumn<PrepRecipeRow>());
  }

  if (options.expandable) {
    cols.push(
      {
        id: "expander",
        header: "",
        cell: ({ row }) => <ExpandCell row={row} />,
        enableSorting: false,
        enableHiding: false,
        size: 40,
      },
      {
        accessorKey: "prepRecipeId",
        meta: { label: i18n._(msg`Prep Recipe ID`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Prep Recipe ID`)} />
        ),
        cell: ({ getValue }) => <TextCell value={getValue() as string} mono />,
      },
    );
  }

  cols.push(
    {
      accessorKey: "imageUrl",
      meta: { label: i18n._(msg`Image`) },
      header: i18n._(msg`Image`),
      cell: ({ row }) => (
        <ImageCell
          src={row.original.imageUrl}
          alt={row.original.name}
          size={options.expandable ? 36 : 44}
          rounded={options.expandable ? undefined : "lg"}
        />
      ),
      enableSorting: false,
      size: options.expandable ? 50 : 60,
    },
    {
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Name`)} />
      ),
      cell: ({ row }) => {
        if (options.expandable) {
          return (
            <span className="font-medium text-sm text-(--color-text-primary)">
              {row.original.name}
            </span>
          );
        }
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-sm text-(--color-text-primary)">
              {row.original.name}
            </span>
            {row.original.prepRecipeId ? (
              <span className="text-xs text-(--color-text-secondary)">
                {row.original.prepRecipeId}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      accessorKey: "timeToPrep",
      meta: { label: i18n._(msg`Time to Prep`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Time to Prep`)} />
      ),
      cell: ({ getValue }) => <TextCell value={getValue() as string} muted />,
    },
    {
      accessorKey: "yield",
      meta: { label: i18n._(msg`Yield`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Yield`)} />
      ),
      cell: ({ getValue }) => <TextCell value={getValue() as string} muted />,
    },
    {
      accessorKey: "ingredientCount",
      meta: { label: i18n._(msg`# Ingredients`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`# Ingredients`)} />
      ),
      cell: ({ getValue }) => <TextCell value={getValue() as number} muted />,
    },
    {
      accessorKey: "prepNotes",
      meta: { label: i18n._(msg`Prep Notes`), defaultVisible: false },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Prep Notes`)} />
      ),
      cell: ({ getValue }) => (
        <TextCell value={getValue() as string} muted truncate maxWidth={180} />
      ),
    },
    {
      accessorKey: "allergenCount",
      meta: { label: i18n._(msg`Allergens`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Allergens`)} />
      ),
      cell: ({ getValue }) => {
        const count = getValue() as number;
        return count > 0 ? (
          <Badge variant="outline">{count}</Badge>
        ) : (
          <TextCell value={null} muted />
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const cb = options.callbacks;
        if (!cb) return <ActionsCell items={[]} />;
        const rec = row.original;
        return (
          <ActionsCell
            items={[
              {
                label: i18n._(msg`Edit item`),
                onClick: () => cb.onEdit(rec),
              },
              {
                label: i18n._(msg`Delete item`),
                destructive: true,
                onClick: () => cb.onDelete(rec),
              },
            ]}
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 48,
    },
  );

  if (options.showOrganization || options.showCompany) {
    const extraCols: ColumnDef<PrepRecipeRow, unknown>[] = [];

    if (options.showOrganization) {
      extraCols.push({
        id: "organizationName",
        accessorFn: (row) => row.organizationName ?? "",
        meta: { label: i18n._(msg`Organization`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Organization`)} />
        ),
        cell: ({ row }) => <TextCell value={row.original.organizationName ?? null} muted />,
      });
    }

    if (options.showCompany) {
      extraCols.push({
        id: "companyName",
        accessorFn: (row) => row.companyName ?? "",
        meta: { label: i18n._(msg`Concept`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Concept`)} />
        ),
        cell: ({ row }) => <TextCell value={row.original.companyName ?? null} muted />,
      });
    }

    const timeToPrepIndex = cols.findIndex((col) => {
      if (typeof col !== "object" || !col) return false;
      return (col as { accessorKey?: string }).accessorKey === "timeToPrep";
    });

    cols.splice(timeToPrepIndex >= 0 ? timeToPrepIndex : cols.length - 1, 0, ...extraCols);
  }

  return options.readOnly ? cols.filter((col) => col.id !== "actions") : cols;
}
